import { useCallback } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  CloneRoutingConfigRequest,
  CloneRoutingConfigResponse,
  CreateRoutingConfigFromDefaultRequest,
  CreateRoutingConfigFromDefaultResponse,
  GetEffectiveTenantRouteResponse,
  GetOrgRoutingAssignmentsResponse,
  GetRoutingConfigSkuSummaryResponse,
  GlobalDefaultParityResponse,
  ListRoutingConfigsResponse,
  ListRoutingSkuTargetsResponse,
  PutRoutingSkuPolicyRequest,
  PutRoutingSkuTargetRequest,
  PutRoutingSkuTargetResponse,
  RenameRoutingConfigRequest,
  RoutingConfigRecord,
  RoutingSkuPolicyRecord,
  RoutingSkuTargetRecord,
  UpstreamProtocol,
  RoutingConfigAssignmentRecord,
  SetGlobalRoutingConfigResponse,
  SetOrgRoutingAssignmentsRequest,
  SetOrgRoutingAssignmentsResponse,
  ApplyRoutingConfigSkuCoverageFillRequest,
  ApplyRoutingConfigSkuCoverageFillResponse,
  PreviewRoutingConfigSkuCoverageFillRequest,
  PreviewRoutingConfigSkuCoverageFillResponse,
  SetRoutingConfigCoverageModeRequest,
  ListRoutingTimeWindowsResponse,
  PutRoutingTimeWindowRequest,
  RoutingTimeWindowRecord,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { SKU_READINESS_QUERY_KEY } from './skuReadiness.ts';
import { SKU_SUPPLY_CANDIDATES_KEY } from './skuSupplyCandidates.ts';
import {
  assignOrgRoutingConfig,
  getOrgRoutingAssignments,
  setOrgRoutingAssignments,
  cloneRoutingConfig,
  createRoutingConfigFromDefault,
  deleteRoutingConfigSkuPolicy,
  deleteRoutingConfigSkuTarget,
  getGlobalDefaultParity,
  deleteRoutingDefaultSkuPolicy,
  deleteRoutingDefaultSkuTarget,
  type EffectiveTenantRouteParams,
  getEffectiveTenantRoute,
  getRoutingConfigSkuPolicy,
  getRoutingConfigSkuSummary,
  getRoutingDefaultSkuPolicy,
  listRoutingConfigs,
  listRoutingConfigSkuTargets,
  listRoutingDefaultSkuTargets,
  renameRoutingConfig,
  setRoutingConfigSkuPolicy,
  setRoutingConfigSkuTarget,
  setRoutingDefaultSkuPolicy,
  setRoutingDefaultSkuTarget,
  setGlobalRoutingConfig,
  applyRoutingConfigSkuCoverageFill,
  previewRoutingConfigSkuCoverageFill,
  setRoutingConfigCoverageMode,
  setRoutingConfigDeprecation,
  deleteRoutingConfigTimeWindow,
  listRoutingConfigTimeWindows,
  setRoutingConfigTimeWindow,
} from './tenantRouting.ts';

/**
 * Root query key for every tenant-routing read — exported so surfaces can
 * force a refetch on partial-failure paths the mutation hooks cannot see
 * (e.g. a PUT that landed followed by a cleanup DELETE that failed).
 */
export const TENANT_ROUTING_KEY = ['tenant-routing'] as const;
const ROUTING_CONFIGS_KEY = [...TENANT_ROUTING_KEY, 'configs'] as const;
// Under the tenant-routing root so a config mutation refetches the comparison
// too — a deprecation or coverage-mode change does not move parity, but a
// target edit made from the same page does.
const GLOBAL_DEFAULT_PARITY_KEY = [...TENANT_ROUTING_KEY, 'global-default-parity'] as const;

/**
 * Query roots that are *derived* from the SKU target pool but do not live
 * under `TENANT_ROUTING_KEY`, so a prefix invalidation cannot reach them
 * (epic #1327 PR-4):
 *
 *   - `sku-supply-candidates` carries a per-candidate `bound` flag computed
 *     from the enabled rows of `routing_default_sku_targets`;
 *   - `sku-readiness` derives its binding / probe / cost bits from the same
 *     rows, for both the SKU detail checklist and the marketplace list
 *     column (#1335) — the id list is part of the key, so invalidating the
 *     root covers every batch shape.
 *
 * Every mutation that writes a default SKU target therefore has to invalidate
 * these too, in both directions: binding from the supply panel flips `bound`
 * on, and deleting the last enabled target in the pool panel flips it off. It
 * lives here rather than at the call site because the panels are not the only
 * writers, and a page that missed it would silently report an unroutable SKU
 * as bound and ready.
 */
const SKU_TARGET_DERIVED_KEYS = [SKU_SUPPLY_CANDIDATES_KEY, SKU_READINESS_QUERY_KEY] as const;

function invalidateSkuTargetQueries(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
  for (const queryKey of SKU_TARGET_DERIVED_KEYS) {
    void qc.invalidateQueries({ queryKey });
  }
}

function effectiveKey(
  params: EffectiveTenantRouteParams | null,
): readonly ['tenant-routing', 'effective', string] {
  return [
    ...TENANT_ROUTING_KEY,
    'effective',
    params === null
      ? ''
      : `${params.org_id}::${params.sku_id}::${params.protocol}`,
  ] as const;
}

function defaultSkuTargetsKey(
  skuId: string,
): readonly ['tenant-routing', 'default-sku-targets', string] {
  return [...TENANT_ROUTING_KEY, 'default-sku-targets', skuId] as const;
}

function configSkuTargetsKey(
  configId: string | null,
  skuId: string,
): readonly ['tenant-routing', 'config-sku-targets', string, string] {
  return [...TENANT_ROUTING_KEY, 'config-sku-targets', configId ?? '', skuId] as const;
}

export function configSkuSummaryKey(
  configId: string | null,
): readonly ['tenant-routing', 'config-sku-summary', string] {
  return [...TENANT_ROUTING_KEY, 'config-sku-summary', configId ?? ''] as const;
}

function orgAssignmentsKey(
  orgId: string,
): readonly ['tenant-routing', 'org-assignments', string] {
  return [...TENANT_ROUTING_KEY, 'org-assignments', orgId] as const;
}

function defaultSkuPolicyKey(
  skuId: string,
): readonly ['tenant-routing', 'default-sku-policy', string] {
  return [...TENANT_ROUTING_KEY, 'default-sku-policy', skuId] as const;
}

function configSkuPolicyKey(
  configId: string | null,
  skuId: string,
): readonly ['tenant-routing', 'config-sku-policy', string, string] {
  return [...TENANT_ROUTING_KEY, 'config-sku-policy', configId ?? '', skuId] as const;
}

export function useRoutingConfigs(): UseQueryResult<ListRoutingConfigsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_CONFIGS_KEY,
    queryFn: ({ signal }) => listRoutingConfigs(fetchImpl, signal),
  });
}

/**
 * On-demand global-default parity (#1918). Not auto-fetched: it is a four-table
 * comparison an operator asks for, not something every page load should pay
 * for. Callers enable it when the dialog opens.
 */
export function useGlobalDefaultParity(enabled: boolean): UseQueryResult<GlobalDefaultParityResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: GLOBAL_DEFAULT_PARITY_KEY,
    queryFn: ({ signal }) => getGlobalDefaultParity(fetchImpl, signal),
    enabled,
  });
}

/** Stamp or clear a config's soft deprecation marker (#1919). */
export function useSetRoutingConfigDeprecation(): UseMutationResult<
  void,
  Error,
  { configId: string; deprecated: boolean }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, deprecated }) => {
      await setRoutingConfigDeprecation(configId, { deprecated }, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

/** Constant-query config SKU partition used by Customer 360 summaries. */
export function useRoutingConfigSkuSummary(
  configId: string | null,
): UseQueryResult<GetRoutingConfigSkuSummaryResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: configSkuSummaryKey(configId),
    queryFn: ({ signal }) => {
      if (configId === null || configId.length === 0) {
        throw new Error('useRoutingConfigSkuSummary: configId is required');
      }
      return getRoutingConfigSkuSummary(configId, fetchImpl, signal);
    },
    enabled: configId !== null && configId.length > 0,
  });
}

/**
 * Returns the whole response, not just `config` (#1384 D09): the copy carries
 * a stranded-pin report the caller has to surface, and a hook that projected
 * it away made that report unreachable from the page.
 */
export function useCreateRoutingConfigFromDefault(): UseMutationResult<
  CreateRoutingConfigFromDefaultResponse,
  Error,
  CreateRoutingConfigFromDefaultRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createRoutingConfigFromDefault(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useCloneRoutingConfig(): UseMutationResult<
  CloneRoutingConfigResponse,
  Error,
  { configId: string } & CloneRoutingConfigRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, ...body }) => cloneRoutingConfig(configId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useAssignOrgRoutingConfig(): UseMutationResult<
  RoutingConfigAssignmentRecord,
  Error,
  { orgId: string; configId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, configId }) => {
      const response = await assignOrgRoutingConfig(configId, orgId, fetchImpl);
      return response.assignment;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useOrgRoutingAssignments(
  orgId: string,
): UseQueryResult<GetOrgRoutingAssignmentsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgAssignmentsKey(orgId),
    queryFn: ({ signal }) => getOrgRoutingAssignments(orgId, fetchImpl, signal),
    enabled: orgId.length > 0,
  });
}

export function useSetOrgRoutingAssignments(): UseMutationResult<
  SetOrgRoutingAssignmentsResponse,
  Error,
  { orgId: string; body: SetOrgRoutingAssignmentsRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => setOrgRoutingAssignments(orgId, body, fetchImpl),
    onSuccess: (data, { orgId }) => {
      qc.setQueryData(orgAssignmentsKey(orgId), { assignments: data.assignments });
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

/**
 * #1421: the switch now carries an operator-typed `reason`, so the mutation
 * variable is no longer a bare config id.
 *
 * The whole response is returned rather than just `assignment` (#1421 P3b):
 * the caller has to tell the operator whether the fleet converged or is still
 * inside the bounded propagation window, and that verdict is in `cache`.
 */
export function useSetGlobalRoutingConfig(): UseMutationResult<
  SetGlobalRoutingConfigResponse,
  Error,
  { configId: string; reason: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, reason }) =>
      setGlobalRoutingConfig(configId, { reason }, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useRenameRoutingConfig(): UseMutationResult<
  RoutingConfigRecord,
  Error,
  { configId: string } & RenameRoutingConfigRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, name }) => {
      const response = await renameRoutingConfig(configId, { name }, fetchImpl);
      return response.config;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useEffectiveTenantRoute(
  params: EffectiveTenantRouteParams | null,
): UseQueryResult<GetEffectiveTenantRouteResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: effectiveKey(params),
    queryFn: ({ signal }) => {
      if (params === null) {
        throw new Error('useEffectiveTenantRoute: params are required');
      }
      return getEffectiveTenantRoute(params, fetchImpl, signal);
    },
    enabled: params !== null,
  });
}

// ─── SKU-keyed pools + policies (epic #898 P5, #909) ────────────────────────

/**
 * Combined result of the per-SKU pool fan-out. The gateway only exposes
 * per-SKU GETs (no list-all endpoint), so surfaces that need the whole
 * matrix issue one query per catalog SKU and merge here. SKUs whose
 * query has not resolved yet are absent from the map.
 */
export interface SkuTargetsMap {
  readonly targetsBySku: ReadonlyMap<string, readonly RoutingSkuTargetRecord[]>;
  readonly liveProtocolsByTargetId: ReadonlyMap<string, readonly UpstreamProtocol[]>;
  /**
   * The SKUs whose own query is currently SUCCEEDED — not merely "has data".
   *
   * React Query keeps the last good payload when a background refetch fails,
   * so `targetsBySku.has(skuId)` cannot tell a confirmed pool from a stale
   * one. Any caller about to decide whether a PUT inserts or updates has to
   * read this instead (#1457). Per SKU, not per map: the fan-out issues one
   * query per catalog SKU, and one unrelated SKU's failure must not disarm
   * every other SKU's write path — nor may it leave the failing SKU armed.
   */
  readonly loadedSkuIds: ReadonlySet<string>;
  readonly isPending: boolean;
  readonly isError: boolean;
}

interface SkuPolicySlice { readonly skuId: string; readonly policy: RoutingSkuPolicyRecord | null }
export interface SkuPoliciesMap {
  readonly policiesBySku: ReadonlyMap<string, RoutingSkuPolicyRecord | null>;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * Folds the per-SKU fan-out into the whole-catalog matrix.
 *
 * The slices cache the raw wire response, not a derived shape: these
 * queries share `defaultSkuTargetsKey` with the single-SKU
 * `useRoutingDefaultSkuTargets` that the detail pages use, and a shared key
 * whose entry could hold either shape crashed the market page whenever the
 * detail page had populated the cache first (#1424). Normalization belongs
 * here, downstream of the cache, so the key keeps exactly one shape.
 *
 * `skuIds` is the same array the queries were built from, so `results` is
 * positionally aligned with it — the response carries no SKU id of its own.
 */
function combineSkuTargetResults(
  results: readonly UseQueryResult<ListRoutingSkuTargetsResponse, Error>[],
  skuIds: readonly string[],
): SkuTargetsMap {
  const targetsBySku = new Map<string, readonly RoutingSkuTargetRecord[]>();
  const liveProtocolsByTargetId = new Map<string, readonly UpstreamProtocol[]>();
  const loadedSkuIds = new Set<string>();
  results.forEach((result, index) => {
    const skuId = skuIds[index];
    if (result.data !== undefined && skuId !== undefined) {
      targetsBySku.set(skuId, result.data.targets);
      const liveProtocols = result.data.live_protocols_by_target_id ?? {};
      for (const [targetId, protocols] of Object.entries(liveProtocols)) {
        liveProtocolsByTargetId.set(targetId, protocols);
      }
    }
    if (result.isSuccess && skuId !== undefined) loadedSkuIds.add(skuId);
  });
  return {
    targetsBySku,
    liveProtocolsByTargetId,
    loadedSkuIds,
    isPending: results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
  };
}

/** Stable empty list so an unselected config doesn't churn the combine identity. */
const NO_SKU_IDS: readonly string[] = [];

export function useRoutingDefaultSkuTargetsMap(skuIds: readonly string[]): SkuTargetsMap {
  const { fetchImpl } = useSession();
  // useQueries only reuses its folded result while `combine` keeps its identity,
  // and the fold returns Maps, which structural sharing can't collapse back. A
  // fresh arrow per render would re-fold the whole catalog on every unrelated
  // state change and invalidate every memo downstream of the map.
  const combine = useCallback(
    (results: readonly UseQueryResult<ListRoutingSkuTargetsResponse, Error>[]): SkuTargetsMap =>
      combineSkuTargetResults(results, skuIds),
    [skuIds],
  );
  return useQueries({
    queries: skuIds.map((skuId) => ({
      queryKey: defaultSkuTargetsKey(skuId),
      queryFn: ({ signal }: { signal: AbortSignal }): Promise<ListRoutingSkuTargetsResponse> =>
        listRoutingDefaultSkuTargets(skuId, fetchImpl, signal),
    })),
    combine,
  });
}

export function useRoutingConfigSkuTargetsMap(
  configId: string | null,
  skuIds: readonly string[],
): SkuTargetsMap {
  const { fetchImpl } = useSession();
  const activeConfigId = configId !== null && configId.length > 0 ? configId : null;
  // Derived from the same condition as the query list, so the combine's
  // positional lookup cannot drift when the config is unselected.
  const activeSkuIds = activeConfigId === null ? NO_SKU_IDS : skuIds;
  const combine = useCallback(
    (results: readonly UseQueryResult<ListRoutingSkuTargetsResponse, Error>[]): SkuTargetsMap =>
      combineSkuTargetResults(results, activeSkuIds),
    [activeSkuIds],
  );
  return useQueries({
    queries: activeConfigId === null
      ? []
      : activeSkuIds.map((skuId) => ({
          queryKey: configSkuTargetsKey(activeConfigId, skuId),
          queryFn: ({ signal }: { signal: AbortSignal }): Promise<ListRoutingSkuTargetsResponse> =>
            listRoutingConfigSkuTargets(activeConfigId, skuId, fetchImpl, signal),
        })),
    combine,
  });
}

export function useRoutingConfigSkuPoliciesMap(
  configId: string | null,
  skuIds: readonly string[],
): SkuPoliciesMap {
  const { fetchImpl } = useSession();
  return useQueries({
    queries: configId === null || configId.length === 0 ? [] : skuIds.map((skuId) => ({
      queryKey: [...configSkuPolicyKey(configId, skuId), 'coverage'],
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<SkuPolicySlice> => ({
        skuId,
        policy: await getRoutingConfigSkuPolicy(configId, skuId, fetchImpl, signal),
      }),
    })),
    combine: (results): SkuPoliciesMap => {
      const policiesBySku = new Map<string, RoutingSkuPolicyRecord | null>();
      for (const result of results) if (result.data !== undefined) policiesBySku.set(result.data.skuId, result.data.policy);
      return {
        policiesBySku,
        isPending: results.some((result) => result.isPending),
        isError: results.some((result) => result.isError),
      };
    },
  });
}

export function useRoutingDefaultSkuTargets(
  skuId: string | null,
): UseQueryResult<ListRoutingSkuTargetsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: defaultSkuTargetsKey(skuId ?? ''),
    queryFn: async ({ signal }) => {
      if (skuId === null || skuId.length === 0) {
        throw new Error('useRoutingDefaultSkuTargets: skuId is required');
      }
      return listRoutingDefaultSkuTargets(skuId, fetchImpl, signal);
    },
    enabled: skuId !== null && skuId.length > 0,
  });
}

export function useSetRoutingDefaultSkuTarget(): UseMutationResult<
  PutRoutingSkuTargetResponse,
  Error,
  { skuId: string; body: PutRoutingSkuTargetRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, body }) => setRoutingDefaultSkuTarget(skuId, body, fetchImpl),
    onSuccess: () => {
      invalidateSkuTargetQueries(qc);
    },
  });
}

export function useDeleteRoutingDefaultSkuTarget(): UseMutationResult<
  void,
  Error,
  { skuId: string; targetId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ skuId, targetId }) => {
      await deleteRoutingDefaultSkuTarget(skuId, targetId, fetchImpl);
    },
    onSuccess: () => {
      invalidateSkuTargetQueries(qc);
    },
  });
}

export function useSetRoutingConfigSkuTarget(): UseMutationResult<
  RoutingSkuTargetRecord,
  Error,
  { configId: string; skuId: string; body: PutRoutingSkuTargetRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, skuId, body }) =>
      setRoutingConfigSkuTarget(configId, skuId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useDeleteRoutingConfigSkuTarget(): UseMutationResult<
  void,
  Error,
  { configId: string; skuId: string; targetId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, skuId, targetId }) => {
      await deleteRoutingConfigSkuTarget(configId, skuId, targetId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useRoutingDefaultSkuPolicy(
  skuId: string | null,
): UseQueryResult<RoutingSkuPolicyRecord | null> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: defaultSkuPolicyKey(skuId ?? ''),
    queryFn: ({ signal }) => {
      if (skuId === null || skuId.length === 0) {
        throw new Error('useRoutingDefaultSkuPolicy: skuId is required');
      }
      return getRoutingDefaultSkuPolicy(skuId, fetchImpl, signal);
    },
    enabled: skuId !== null && skuId.length > 0,
  });
}

export function useSetRoutingDefaultSkuPolicy(): UseMutationResult<
  RoutingSkuPolicyRecord,
  Error,
  { skuId: string; body: PutRoutingSkuPolicyRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, body }) => setRoutingDefaultSkuPolicy(skuId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useDeleteRoutingDefaultSkuPolicy(): UseMutationResult<
  void,
  Error,
  { skuId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ skuId }) => {
      await deleteRoutingDefaultSkuPolicy(skuId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useSetRoutingConfigSkuPolicy(): UseMutationResult<
  RoutingSkuPolicyRecord,
  Error,
  { configId: string; skuId: string; body: PutRoutingSkuPolicyRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, skuId, body }) =>
      setRoutingConfigSkuPolicy(configId, skuId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useDeleteRoutingConfigSkuPolicy(): UseMutationResult<
  void,
  Error,
  { configId: string; skuId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, skuId }) => {
      await deleteRoutingConfigSkuPolicy(configId, skuId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useSetRoutingConfigCoverageMode(): UseMutationResult<
  RoutingConfigRecord,
  Error,
  { configId: string; body: SetRoutingConfigCoverageModeRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, body }) => (await setRoutingConfigCoverageMode(configId, body, fetchImpl)).config,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY }); },
  });
}

export function usePreviewRoutingConfigSkuCoverageFill(): UseMutationResult<
  PreviewRoutingConfigSkuCoverageFillResponse, Error, PreviewRoutingConfigSkuCoverageFillRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({ mutationFn: (body) => previewRoutingConfigSkuCoverageFill(body, fetchImpl) });
}

export function useApplyRoutingConfigSkuCoverageFill(): UseMutationResult<
  ApplyRoutingConfigSkuCoverageFillResponse, Error, ApplyRoutingConfigSkuCoverageFillRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => applyRoutingConfigSkuCoverageFill(body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY }); },
  });
}


// ─── Routing time windows (0044) ────────────────────────────────────────────

function configTimeWindowsKey(
  configId: string | null,
): readonly ['tenant-routing', 'config-time-windows', string] {
  return [...TENANT_ROUTING_KEY, 'config-time-windows', configId ?? ''] as const;
}

/**
 * One read per config, not per SKU: a window is a *group* whose overrides may
 * span every SKU in the pool, so fanning this out per SKU would fetch the same
 * rows once per model and still have to be reassembled to answer "what else
 * does this schedule move".
 */
export function useRoutingConfigTimeWindows(
  configId: string | null,
): UseQueryResult<ListRoutingTimeWindowsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: configTimeWindowsKey(configId),
    queryFn: ({ signal }) => {
      if (configId === null || configId.length === 0) {
        throw new Error('useRoutingConfigTimeWindows: configId is required');
      }
      return listRoutingConfigTimeWindows(configId, fetchImpl, signal);
    },
    enabled: configId !== null && configId.length > 0,
  });
}

export function useSetRoutingConfigTimeWindow(): UseMutationResult<
  RoutingTimeWindowRecord,
  Error,
  { configId: string; windowId: string | null; body: PutRoutingTimeWindowRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, windowId, body }) =>
      setRoutingConfigTimeWindow(configId, windowId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}

export function useDeleteRoutingConfigTimeWindow(): UseMutationResult<
  void,
  Error,
  { configId: string; windowId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, windowId }) => {
      await deleteRoutingConfigTimeWindow(configId, windowId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
    },
  });
}
